import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface EarnModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function EarnModal({ onClose, onSuccess }: EarnModalProps) {
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('worship');
  const [loading, setLoading] = useState(false);

  const categories = [
    { value: 'worship', label: '예배 참석', icon: 'ri-church-line' },
    { value: 'volunteer', label: '봉사 활동', icon: 'ri-hand-heart-line' },
    { value: 'study', label: '성경 공부', icon: 'ri-book-open-line' },
    { value: 'prayer', label: '기도 모임', icon: 'ri-hands-pray-line' },
    { value: 'mission', label: '선교 활동', icon: 'ri-global-line' },
    { value: 'event', label: '행사 참여', icon: 'ri-calendar-event-line' },
    { value: 'content', label: '콘텐츠 시청', icon: 'ri-play-circle-line' },
    { value: 'other', label: '기타', icon: 'ri-more-line' },
  ];

  const quickAmounts = [100, 500, 1000, 2000, 5000, 10000];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !amount || !description) {
      alert('모든 항목을 입력해주세요.');
      return;
    }

    const earnAmount = parseInt(amount);
    if (earnAmount <= 0 || earnAmount > 100000) {
      alert('적립 금액은 1 ~ 100,000 달란트 사이여야 합니다.');
      return;
    }

    setLoading(true);

    try {
      // 달란트 포인트 업데이트
      const { data: currentPoints, error: fetchError } = await supabase
        .from('talent_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;

      const newBalance = (currentPoints?.balance || 0) + earnAmount;
      const newTotalEarned = (currentPoints?.total_earned || 0) + earnAmount;

      const { error: updateError } = await supabase
        .from('talent_points')
        .update({
          balance: newBalance,
          total_earned: newTotalEarned,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // 거래 내역 추가
      const { error: transactionError } = await supabase
        .from('point_transactions')
        .insert({
          user_id: user.id,
          amount: earnAmount,
          type: 'earn',
          description: `${categories.find(c => c.value === category)?.label}: ${description}`,
          created_at: new Date().toISOString(),
        });

      if (transactionError) throw transactionError;

      alert(`${earnAmount.toLocaleString()} 달란트가 적립되었습니다!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('달란트 적립 실패:', error);
      alert('달란트 적립에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <i className="ri-add-circle-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">달란트 적립</h2>
                <p className="text-green-100 text-sm">신앙 활동으로 달란트를 적립하세요</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              활동 카테고리
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    category === cat.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <i className={`${cat.icon} text-2xl mb-2 ${
                    category === cat.value ? 'text-green-600' : 'text-gray-400'
                  }`}></i>
                  <p className={`text-sm font-semibold ${
                    category === cat.value ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {cat.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              적립 금액
            </label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
              {quickAmounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setAmount(amt.toString())}
                  className="py-3 px-4 bg-gray-100 hover:bg-green-50 hover:text-green-600 hover:border-green-500 border-2 border-transparent rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  {amt.toLocaleString()}
                </button>
              ))}
            </div>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="직접 입력"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none text-lg"
                min="1"
                max="100000"
                required
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                달란트
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              * 1회 최대 100,000 달란트까지 적립 가능합니다
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              활동 내용
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="어떤 활동으로 달란트를 적립하셨나요?"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none resize-none"
              rows={4}
              maxLength={200}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {description.length}/200자
            </p>
          </div>

          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-xl text-green-600 mt-0.5"></i>
              <div className="text-sm text-green-800">
                <p className="font-semibold mb-1">적립 안내</p>
                <ul className="space-y-1 text-green-700">
                  <li>• 신앙 활동 참여 시 달란트를 적립할 수 있습니다</li>
                  <li>• 적립된 달란트는 상품 구매, 기부 등에 사용 가능합니다</li>
                  <li>• 부정한 방법으로 적립 시 회수될 수 있습니다</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {loading ? '처리 중...' : '적립하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
